import React, {useEffect, useState, useContext, useRef} from "react";
import * as Sentry from '@sentry/browser';
import Api from "../../Api";
import Pagination from "react-js-pagination";
import MessageContext from "../../MessageContext";

import classes from "./styles.module.css";

var LEDGER_TYPE_LABELS = {
    1: "Зачисление",
    2: "Комиссия",
    3: "Выплата мерчанту",
    4: "Отмена выплаты",
    5: "Ручная корректировка",
    6: "Возврат",
    7: "Чарджбэк",
    8: "Пополнение"
};

function getLedgerTypeClass(type) {
    switch (type) {
        case 8:
            return classes.TypeTopup;
        case 5:
            return classes.TypeAdjustment;
        case 1:
            return classes.TypeTransaction;
        case 3: case 4:
            return classes.TypeSettlement;
        case 2:
            return classes.TypeCommission;
        case 6: case 7:
            return classes.TypeSettlement;
        default:
            return classes.TypeBadge;
    }
}

function formatAmount(amountInKopecks) {
    if (amountInKopecks === null || amountInKopecks === undefined) return "0.00";
    return (Number(amountInKopecks) / 100).toLocaleString("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function formatDate(dateStr) {
    if (!dateStr) return "\u2014";
    try {
        var d = new Date(dateStr);
        return d.toLocaleString("ru-RU");
    } catch (e) {
        return dateStr;
    }
}

function TerminalBalanceTab(props) {
    var token = props.token;
    var terminal = props.terminal;
    var terminalId = terminal && terminal.id ? terminal.id : null;
    var onMessage = useContext(MessageContext).onMessage;
    var mounted = useRef(true);

    // Balances
    var _balances = useState(null);
    var balances = _balances[0];
    var setBalances = _balances[1];

    var _balancesLoading = useState(false);
    var balancesLoading = _balancesLoading[0];
    var setBalancesLoading = _balancesLoading[1];

    // MerchantSettings list and selected
    var _msList = useState([]);
    var msList = _msList[0];
    var setMsList = _msList[1];

    var _selectedMsId = useState("");
    var selectedMsId = _selectedMsId[0];
    var setSelectedMsId = _selectedMsId[1];

    // Ledger
    var _ledger = useState([]);
    var ledger = _ledger[0];
    var setLedger = _ledger[1];

    var _ledgerCount = useState(0);
    var ledgerCount = _ledgerCount[0];
    var setLedgerCount = _ledgerCount[1];

    var _ledgerPage = useState(1);
    var ledgerPage = _ledgerPage[0];
    var setLedgerPage = _ledgerPage[1];

    var _ledgerTypeFilter = useState("");
    var ledgerTypeFilter = _ledgerTypeFilter[0];
    var setLedgerTypeFilter = _ledgerTypeFilter[1];

    var _ledgerLoading = useState(false);
    var ledgerLoading = _ledgerLoading[0];
    var setLedgerLoading = _ledgerLoading[1];

    // Manual entry form (adjustment only)
    var _entryAmount = useState("");
    var entryAmount = _entryAmount[0];
    var setEntryAmount = _entryAmount[1];

    var _entryDescription = useState("");
    var entryDescription = _entryDescription[0];
    var setEntryDescription = _entryDescription[1];

    var _entrySubmitting = useState(false);
    var entrySubmitting = _entrySubmitting[0];
    var setEntrySubmitting = _entrySubmitting[1];

    var ledgerPerPage = 15;

    useEffect(function () {
        mounted.current = true;
        return function () {
            mounted.current = false;
        };
    }, []);

    // Load data when terminal changes
    useEffect(function () {
        if (terminalId) {
            loadBalances();
            loadLedger();
        }
    }, [terminalId, token]);

    // Reload ledger when page, filter or selected MS changes
    useEffect(function () {
        if (terminalId && selectedMsId) {
            loadLedger();
        }
    }, [ledgerPage, ledgerTypeFilter, selectedMsId]);

    function loadBalances() {
        setBalancesLoading(true);
        new Api({token: token}).getTerminalBalances(terminalId).then(function (r) {
            if (mounted.current) {
                var data = r.data || r;
                setBalances(data);

                // Save MerchantSettings list from balances response
                var balancesList = Array.isArray(data.balances) ? data.balances : [];
                setMsList(balancesList);
                // Auto-select first if none selected
                if (balancesList.length > 0 && !selectedMsId) {
                    setSelectedMsId(String(balancesList[0].merchantSettingsId));
                }

                setBalancesLoading(false);
            }
        }).catch(function (err) {
            Sentry.captureException(err);
            if (mounted.current) {
                setBalancesLoading(false);
                onMessage({type: "error", message: "Ошибка загрузки балансов"});
            }
        });
    }

    function loadLedger() {
        if (!selectedMsId) return;
        setLedgerLoading(true);
        var filters = {
            merchant_settings_id: selectedMsId,
            page: ledgerPage,
            limit: ledgerPerPage
        };
        if (ledgerTypeFilter) filters.type = ledgerTypeFilter;

        new Api({token: token}).getTerminalLedger(terminalId, filters).then(function (r) {
            if (mounted.current) {
                setLedger(Array.isArray(r.data) ? r.data : []);
                setLedgerCount(typeof r.count === "number" ? r.count : 0);
                setLedgerLoading(false);
            }
        }).catch(function (err) {
            Sentry.captureException(err);
            if (mounted.current) {
                setLedger([]);
                setLedgerCount(0);
                setLedgerLoading(false);
            }
        });
    }

    function onCreateEntry() {
        if (!entryAmount || isNaN(Number(entryAmount)) || Number(entryAmount) === 0) {
            onMessage({type: "error", message: "Укажите сумму"});
            return;
        }
        if (!selectedMsId) {
            onMessage({type: "error", message: "Выберите MerchantSettings"});
            return;
        }
        setEntrySubmitting(true);
        var data = {
            merchant_settings_id: Number(selectedMsId),
            type: "adjustment",
            amount: Math.round(Number(entryAmount) * 100), // convert to kopecks
            description: entryDescription || ""
        };

        new Api({token: token}).createTerminalLedgerEntry(terminalId, data).then(function () {
            if (mounted.current) {
                setEntrySubmitting(false);
                setEntryAmount("");
                setEntryDescription("");
                loadBalances();
                loadLedger();
                onMessage({type: "success", message: "Запись создана"});
            }
        }).catch(function (err) {
            Sentry.captureException(err);
            if (mounted.current) {
                setEntrySubmitting(false);
                onMessage({type: "error", message: "Ошибка при создании записи"});
            }
        });
    }

    if (!terminalId) {
        return null;
    }

    var totals = balances && balances.totals ? balances.totals : null;
    var transactionBalance = totals && totals.transactionBalance != null ? totals.transactionBalance : null;
    var settlementBalance = totals && totals.settlementBalance != null ? totals.settlementBalance : null;
    var actualBalance = totals && totals.actualBalance != null ? totals.actualBalance : null;

    return (
        <div className={classes.Card}>
            <div className={classes.Content}>
                {balancesLoading && (
                    <div className={classes.LoadingOverlay}>
                        <div className={classes.Spinner}></div>
                        <span>Загрузка...</span>
                    </div>
                )}

                {/* Balances Section */}
                <h4 className={classes.BlockTitle}>Баланс</h4>

                <div className={classes.BalancesRow}>
                    <div className={classes.BalanceCard}>
                        <div className={classes.BalanceLabel}>
                            {totals && totals.labels ? totals.labels.transactionBalance : "Баланс"}
                        </div>
                        <div className={transactionBalance !== null && transactionBalance >= 0 ? classes.BalanceValuePositive : classes.BalanceValueNegative}>
                            {transactionBalance !== null ? formatAmount(transactionBalance) : "\u2014"} &#8381;
                        </div>
                    </div>
                    <div className={classes.BalanceCard}>
                        <div className={classes.BalanceLabel}>
                            {totals && totals.labels ? totals.labels.settlementBalance : "Выплачено мерчанту"}
                        </div>
                        <div className={classes.BalanceValue}>
                            {settlementBalance !== null ? formatAmount(settlementBalance) : "\u2014"} &#8381;
                        </div>
                    </div>
                    <div className={classes.BalanceCard}>
                        <div className={classes.BalanceLabel}>
                            {totals && totals.labels ? totals.labels.actualBalance : "Доступный баланс"}
                        </div>
                        <div className={actualBalance !== null && actualBalance >= 0 ? classes.BalanceValuePositive : classes.BalanceValueNegative}>
                            {actualBalance !== null ? formatAmount(actualBalance) : "\u2014"} &#8381;
                        </div>
                    </div>
                </div>

                <div className={classes.Divider}/>

                {/* MerchantSettings selector (if multiple) */}
                {msList.length > 1 && (
                    <div style={{marginBottom: 16}}>
                        <label style={{fontWeight: 600, fontSize: "0.875rem", marginBottom: 4, display: "block"}}>
                            Мерчант (MerchantSettings)
                        </label>
                        <select
                            className="form-select"
                            value={selectedMsId}
                            onChange={function (e) { setSelectedMsId(e.target.value); setLedgerPage(1); }}
                        >
                            {msList.map(function (ms) {
                                return (
                                    <option key={ms.merchantSettingsId} value={ms.merchantSettingsId}>
                                        {"MS #" + ms.merchantSettingsId + " (баланс: " + formatAmount(ms.transactionBalance) + " \u20BD)"}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}

                {/* Ledger Section */}
                <h5 className={classes.BlockTitle} style={{marginTop: 0}}>Журнал операций</h5>

                <div className={classes.FilterBar}>
                    <select
                        value={ledgerTypeFilter}
                        onChange={function (e) { setLedgerTypeFilter(e.target.value); setLedgerPage(1); }}
                    >
                        <option value="">Все типы</option>
                        <option value="1">Зачисление</option>
                        <option value="2">Комиссия</option>
                        <option value="3">Выплата мерчанту</option>
                        <option value="4">Отмена выплаты</option>
                        <option value="5">Ручная корректировка</option>
                        <option value="6">Возврат</option>
                        <option value="7">Чарджбэк</option>
                        <option value="8">Пополнение</option>
                    </select>
                </div>

                {ledgerLoading ? (
                    <div className={classes.EmptyText}>Загрузка...</div>
                ) : ledger.length === 0 ? (
                    <div className={classes.EmptyText}>Записи не найдены</div>
                ) : (
                    <div>
                        <div className={classes.TableScroll}>
                        <table className={classes.Table}>
                            <thead>
                            <tr>
                                <th>Дата</th>
                                <th>Тип</th>
                                <th>Сумма</th>
                                <th>Баланс после</th>
                                <th>Описание</th>
                                <th>Создал</th>
                            </tr>
                            </thead>
                            <tbody>
                            {ledger.map(function (entry) {
                                var entryId = entry.id || (entry.created_at + "_" + entry.amount);
                                return (
                                    <tr key={entryId}>
                                        <td>{formatDate(entry.created_at || entry.createdAt)}</td>
                                        <td>
                                            <span className={getLedgerTypeClass(entry.type)}>
                                                {entry.type_name || LEDGER_TYPE_LABELS[entry.type] || entry.type || "\u2014"}
                                            </span>
                                        </td>
                                        <td style={{fontWeight: 500}}>
                                            {formatAmount(entry.amount)} &#8381;
                                        </td>
                                        <td>
                                            {formatAmount(entry.balance_after || entry.balanceAfter)} &#8381;
                                        </td>
                                        <td>{entry.description || "\u2014"}</td>
                                        <td>{(entry.created_by && typeof entry.created_by === 'object') ? entry.created_by.username : (entry.created_by || entry.createdBy || "\u2014")}</td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                        </div>

                        {ledgerPerPage < ledgerCount && (
                            <div className={classes.PaginationWrap}>
                                <Pagination
                                    firstPageText={"\u21E4"}
                                    prevPageText={"\u21E0"}
                                    lastPageText={"\u21E5"}
                                    nextPageText={"\u21E2"}
                                    activeLinkClass={classes.ActiveLink}
                                    activePage={ledgerPage}
                                    itemsCountPerPage={ledgerPerPage}
                                    totalItemsCount={ledgerCount}
                                    pageRangeDisplayed={5}
                                    onChange={function (p) { setLedgerPage(p); }}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className={classes.Divider}/>

                {/* Manual Adjustment Section */}
                <h5 className={classes.BlockTitle} style={{marginTop: 0}}>Ручная корректировка</h5>

                <div className={classes.Section}>
                    <div className={classes.FormGrid}>
                        <div className={classes.FormGroup}>
                            <label>Сумма (в рублях)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={entryAmount}
                                onChange={function (e) { setEntryAmount(e.target.value); }}
                                placeholder="0.00"
                                disabled={entrySubmitting}
                            />
                        </div>
                        <div className={classes.FormGroup}>
                            <label>Описание</label>
                            <input
                                type="text"
                                value={entryDescription}
                                onChange={function (e) { setEntryDescription(e.target.value); }}
                                placeholder="Причина корректировки"
                                disabled={entrySubmitting}
                            />
                        </div>
                    </div>
                    <div style={{textAlign: "right", marginTop: 8}}>
                        <button
                            className={classes.BtnSuccess}
                            onClick={onCreateEntry}
                            disabled={entrySubmitting || !entryAmount}
                        >
                            {entrySubmitting ? "Создание..." : "Создать корректировку"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TerminalBalanceTab;



// WEBPACK FOOTER //
// ./src/Components/TerminalBalanceTab/index.js